import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useCallback, useContext, useMemo, type ReactNode } from 'react'
import { ChatContext } from '../../../context/chatContextCore'
import type { TimelineElement } from '../../../context/TimelineContext'
import type { Workspace, WorkspaceRole } from '../../../context/WorkspaceContext'
import { useNls } from '../../../nls/nlsProvider'
import type { AnalysisIncludedSystemKey, AnalysisRecommendation, AnalysisSnapshot, AnalysisTimeRangeKey } from '../../../types/analysis'
import { MetricCard } from '../MetricCard'

import { isSparklineAnomaly, LiveModuleCard, useAssigneeLoadModule, useContributionActivityModule, useRoleLoadModule } from '../modules'
import { buildLiveModuleDescriptor, buildMetricDescriptor } from '../popover/descriptors'

import { formatNumber } from './analysisTabShared'
import type { AnalysisNavigate } from './analysisTabShared'

export const TeamTab = ({
  workspace,
  role: _role,
  timeRange,
  elements,
  snapshot,
  snapshotNonce,
  onNavigate,
  onOpenReports,
  includedSystems,
  highlightAnomalies,
  memberDense = false,
}: {
  workspace: Workspace
  role: WorkspaceRole
  timeRange: AnalysisTimeRangeKey
  elements: TimelineElement[]
  snapshot: AnalysisSnapshot
  snapshotNonce: number
  onNavigate: AnalysisNavigate
  onOpenReports: () => void
  includedSystems?: Set<AnalysisIncludedSystemKey>
  highlightAnomalies?: boolean
  memberDense?: boolean
}) => {
  void _role
  const { t } = useNls()
  const chatContext = useContext(ChatContext)
  const members = useMemo(() => chatContext?.members ?? [], [chatContext?.members])
  const resolveMemberLabel = useCallback((uid: string) => members.find((m) => m.uid === uid || m.id === uid)?.name ?? null, [members])

  const roleLoad = useRoleLoadModule({
    workspaceId: workspace.id,
    members,
    elements,
    timeRange,
    refreshKey: snapshotNonce,
  })

  const contributionActivity = useContributionActivityModule({
    workspaceId: workspace.id,
    elements,
    timeRange,
    resolveMemberLabel,
    refreshKey: snapshotNonce,
  })

  const assigneeLoad = useAssigneeLoadModule({
    workspaceId: workspace.id,
    snapshot,
    elements,
    timeRange,
    refreshKey: snapshotNonce,
  })

  const teamMetrics: Array<{
    title: string
    value: string
    detail: string
    icon: ReactNode
    tone?: 'default' | 'warn' | 'good'
    numeric: number
    drillTarget: AnalysisRecommendation['drillTarget'] | null
  }> = [
    {
      title: 'Assigned checklist work',
      value: formatNumber(snapshot.team.assigneeLoad.reduce((sum, row) => sum + row.total, 0)),
      detail: 'Across visible workspace items',
      icon: <GroupsRoundedIcon />,
      numeric: snapshot.team.assigneeLoad.reduce((sum, row) => sum + row.total, 0),
      drillTarget: 'CHECKLISTS',
    },
    {
      title: 'Unassigned',
      value: formatNumber(snapshot.team.unassigned),
      detail: 'Checklist items without assignee',
      icon: <WarningAmberRoundedIcon />,
      tone: snapshot.team.unassigned ? ('warn' as const) : ('good' as const),
      numeric: snapshot.team.unassigned,
      drillTarget: 'CHECKLISTS',
    },
    {
      title: 'Review pressure',
      value: formatNumber(snapshot.governance.highPriority),
      detail: 'High-priority active records',
      icon: <PolicyRoundedIcon />,
      numeric: snapshot.governance.highPriority,
      drillTarget: 'CHECKLISTS',
    },
  ]
  const memberEmptyState = memberDense
    ? { heading: t('NLSAnalysisMobileNoSignalsHeading'), detail: t('NLSAnalysisMobileNoSignalsDetail') }
    : undefined
  if (includedSystems && !includedSystems.has('TEAM')) {
    return (
      <div className="analysisFilteredStateEmpty" data-testid="team-filtered-empty">
        <strong>{t('NLSAnalysisFilteredEmptyHeading')}</strong>
        <p>{t('NLSAnalysisFilteredEmptyDesc')}</p>
      </div>
    )
  }

  return (
    <div className="analysisStageStack">
      <div className="analysisMetricGrid">
        {teamMetrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            detail={metric.detail}
            icon={metric.icon}
            tone={metric.tone}
            descriptor={buildMetricDescriptor({
              snapshot,
              tab: 'TEAM',
              title: metric.title,
              value: metric.numeric,
              detail: metric.detail,
              drillTarget: metric.drillTarget,
              onNavigate,
              onOpenReports,
            })}
          />
        ))}
      </div>
      <div className={`analysisPhaseGrid${memberDense ? ' analysisPhaseGrid--memberDense' : ''}`}>
        <LiveModuleCard
          data-testid="live-module-assignee-load"
          icon={<GroupsRoundedIcon />}
          title={t('NLSAnalysisModuleAssigneeLoadTitle')}
          eyebrow={t('NLSAnalysisLiveModuleEyebrowTeam')}
          headline={assigneeLoad.headline}
          sparkline={assigneeLoad.sparkline}
          records={assigneeLoad.records}
          emptyState={memberEmptyState}
          loading={assigneeLoad.loading}
          highlighted={highlightAnomalies && isSparklineAnomaly(assigneeLoad.sparkline.points)}
          onRecordClick={() => onNavigate('CHECKLISTS')}
          descriptor={buildLiveModuleDescriptor({
            snapshot,
            id: `assignee-load-${workspace.id}`,
            title: 'Assignee load',
            tab: 'TEAM',
            cardLabel: 'Assignee load',
            points: assigneeLoad.sparkline.points,
            records: assigneeLoad.records.map((record) => ({
              id: record.id,
              primary: record.primary,
              secondary: record.secondary,
              numeric: record.numeric,
              drillTarget: 'CHECKLISTS',
            })),
            direction: assigneeLoad.sparkline.direction,
            onNavigate,
            onOpenReports,
          })}
        />
        <LiveModuleCard
          data-testid="live-module-role-load"
          icon={<ShieldRoundedIcon />}
          title={t('NLSAnalysisModuleRoleLoadTitle')}
          eyebrow={t('NLSAnalysisLiveModuleEyebrowTeam')}
          headline={roleLoad.headline}
          sparkline={roleLoad.sparkline}
          records={roleLoad.records}
          emptyState={memberEmptyState}
          loading={roleLoad.loading}
          highlighted={highlightAnomalies && isSparklineAnomaly(roleLoad.sparkline.points)}
          onRecordClick={() => onNavigate('CHECKLISTS')}
          descriptor={buildLiveModuleDescriptor({
            snapshot,
            id: `role-load-${workspace.id}`,
            title: 'Role load',
            tab: 'TEAM',
            cardLabel: 'Role load',
            points: roleLoad.sparkline.points,
            records: roleLoad.records.map((r) => ({
              id: r.id,
              primary: r.primary,
              secondary: r.secondary,
              numeric: r.numeric,
              drillTarget: 'CHECKLISTS',
            })),
            direction: roleLoad.sparkline.direction,
            onNavigate,
            onOpenReports,
          })}
        />
        <LiveModuleCard
          data-testid="live-module-contribution-activity"
          icon={<TrackChangesRoundedIcon />}
          title={t('NLSAnalysisModuleContributionActivityTitle')}
          eyebrow={t('NLSAnalysisLiveModuleEyebrowTeam')}
          headline={contributionActivity.headline}
          sparkline={contributionActivity.sparkline}
          records={contributionActivity.records}
          emptyState={memberEmptyState}
          loading={contributionActivity.loading}
          highlighted={highlightAnomalies && isSparklineAnomaly(contributionActivity.sparkline.points)}
          onRecordClick={() => onNavigate('CHECKLISTS')}
          descriptor={buildLiveModuleDescriptor({
            snapshot,
            id: `contribution-activity-${workspace.id}`,
            title: 'Contribution activity',
            tab: 'TEAM',
            cardLabel: 'Contribution activity',
            points: contributionActivity.sparkline.points,
            records: contributionActivity.records.map((r) => ({
              id: r.id,
              primary: r.primary,
              secondary: r.secondary,
              numeric: r.numeric,
              drillTarget: 'CHECKLISTS',
            })),
            direction: contributionActivity.sparkline.direction,
            onNavigate,
            onOpenReports,
          })}
        />
      </div>
    </div>
  )
}
